import React, { useState, useEffect } from 'react';
import { Language } from '../../types';
import { TRANSLATIONS } from '../../translations';

interface MultiStepLeadFormProps {
  language: Language;
  onComplete: () => void;
}

const TOTAL_STEPS = 9;

export const MultiStepLeadForm: React.FC<MultiStepLeadFormProps> = ({ onComplete, language }) => {
  const t = TRANSLATIONS[language]?.ownerPartnership?.form || TRANSLATIONS[Language.EN].ownerPartnership.form;
  
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<Record<string, any>>({
    projectType: '',
    projectStage: '',
    projectLocation: '',
    budget: '',
    services: [] as string[],
    priorities: [] as string[],
    timeline: '',
    additionalInfo: '',
    name: '',
    email: '',
    phone: ''
  });
  
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [draftData, setDraftData] = useState<any>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Load progress from storage
  useEffect(() => {
    const saved = localStorage.getItem('ownerPartnershipLeadForm') || sessionStorage.getItem('ownerPartnershipLeadForm');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const now = new Date().getTime();
        // 30 days = 30 * 24 * 60 * 60 * 1000 = 2592000000 ms
        if (parsed.timestamp && now - parsed.timestamp > 2592000000) {
          localStorage.removeItem('ownerPartnershipLeadForm');
          sessionStorage.removeItem('ownerPartnershipLeadForm');
        } else if (parsed.step > 1 || (parsed.data && Object.values(parsed.data).some(v => Array.isArray(v) ? (v as any[]).length > 0 : !!v))) {
          setDraftData(parsed);
          setShowResumePrompt(true);
        }
      } catch (e) {
        // ignore
      }
    }
  }, []);

  // Save progress
  useEffect(() => {
    if (showResumePrompt || showResetConfirm) return;
    
    const timeoutId = setTimeout(() => {
      const dataToSave = {
        step: currentStep,
        data: formData,
        timestamp: new Date().getTime()
      };
      localStorage.setItem('ownerPartnershipLeadForm', JSON.stringify(dataToSave));
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [currentStep, formData, showResumePrompt, showResetConfirm]);

  const handleResumeDraft = () => {
    if (draftData) {
      if (draftData.step) setCurrentStep(draftData.step);
      if (draftData.data) setFormData(prev => ({ ...prev, ...draftData.data }));
    }
    setShowResumePrompt(false);
  };

  const handleStartNew = () => {
    localStorage.removeItem('ownerPartnershipLeadForm');
    sessionStorage.removeItem('ownerPartnershipLeadForm');
    setDraftData(null);
    setShowResumePrompt(false);
  };

  const handleResetConfirm = () => {
    localStorage.removeItem('ownerPartnershipLeadForm');
    sessionStorage.removeItem('ownerPartnershipLeadForm');
    setCurrentStep(1);
    setFormData({
        projectType: '',
        projectStage: '',
        projectLocation: '',
        budget: '',
        services: [],
        priorities: [],
        timeline: '',
        additionalInfo: '',
        name: '',
        email: '',
        phone: ''
    });
    setShowResetConfirm(false);
  };

  const validateStep = () => {
    setError('');
    switch (currentStep) {
      case 1:
        if (!formData.projectType) return false;
        break;
      case 2:
        if (!formData.projectStage) return false;
        break;
      case 3:
        if (!formData.projectLocation.trim()) return false;
        break;
      case 4:
        if (!formData.budget) return false;
        break;
      case 5:
        if (formData.services.length === 0) return false;
        break;
      case 6:
        if (formData.priorities.length === 0) return false;
        break;
      case 7:
        if (!formData.timeline) return false;
        break;
      case 8:
        // Optional
        break;
      case 9:
        if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) return false;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
           setError(t.emailError);
           return false;
        }
        break;
    }
    return true;
  };

  const submitForm = async () => {
    setIsSubmitting(true);
    setError('');
    try {
      const response = await fetch('/api/consultation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      let result;
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        result = await response.json();
      } else {
        // Backend not found or returning HTML
        throw new Error("Submission service is currently under development.");
      }
      
      if (!response.ok) {
        throw new Error(result?.error || 'Failed to submit form');
      }

      localStorage.removeItem('ownerPartnershipLeadForm');
      sessionStorage.removeItem('ownerPartnershipLeadForm');
      
      setCurrentStep(1);
      setFormData({
        projectType: '',
        projectStage: '',
        projectLocation: '',
        budget: '',
        services: [],
        priorities: [],
        timeline: '',
        additionalInfo: '',
        name: '',
        email: '',
        phone: ''
      });
      onComplete();
    } catch (err: any) {
      setError(err.message || t.submitError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (!validateStep()) {
      if (!error) setError(t.error || "Please complete all required fields.");
      return;
    }
    
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(prev => prev + 1);
    } else {
      submitForm();
    }
  };

  const handlePrev = () => {
    setError('');
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const updateData = (key: string, value: any) => {
    setError('');
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const toggleArrayItem = (key: string, item: string) => {
    setError('');
    setFormData(prev => {
      const arr = prev[key] as string[];
      if (arr.includes(item)) {
        return { ...prev, [key]: arr.filter(i => i !== item) };
      } else {
        return { ...prev, [key]: [...arr, item] };
      }
    });
  };

  const renderSingleSelect = (key: string, options: string[]) => (
    <div className="space-y-4">
      {options.map(opt => (
        <button
          key={opt}
          onClick={() => updateData(key, opt)}
          className={`w-full text-left px-6 py-4 border transition-colors ${formData[key] === opt ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-400'}`}
        >
          {opt}
        </button>
      ))}
    </div>
  );

  const renderMultiSelect = (key: string, options: string[]) => (
    <div className="space-y-4">
      {options.map(opt => {
        const isSelected = (formData[key] as string[]).includes(opt);
        return (
          <button
            key={opt}
            onClick={() => toggleArrayItem(key, opt)}
            className={`w-full flex items-center justify-between px-6 py-4 border transition-colors ${isSelected ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-400'}`}
          >
            <span>{opt}</span>
            <div className={`w-5 h-5 border flex items-center justify-center ${isSelected ? 'border-neutral-900 bg-neutral-900' : 'border-neutral-300'}`}>
               {isSelected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
            </div>
          </button>
        )
      })}
    </div>
  );

  if (showResumePrompt) {
    return (
      <div className="w-full max-w-2xl mx-auto bg-white p-8 md:p-12 shadow-xl border-t-4 border-neutral-900 animate-in fade-in">
        <h3 className="text-2xl font-heading uppercase tracking-widest mb-6">Continue your previous enquiry?</h3>
        <p className="text-lg text-neutral-600 mb-8">
          We found an unfinished enquiry saved on this device. Would you like to continue where you left off?
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <button 
            onClick={handleResumeDraft}
            className="bg-neutral-900 text-white uppercase tracking-widest text-xs font-bold px-8 py-4 hover:bg-neutral-800 transition-colors w-full sm:w-auto text-center"
          >
            Continue
          </button>
          <button 
            onClick={handleStartNew}
            className="border border-neutral-300 text-neutral-600 uppercase tracking-widest text-xs font-bold px-8 py-4 hover:bg-neutral-50 transition-colors w-full sm:w-auto text-center"
          >
            Start a New Enquiry
          </button>
        </div>
      </div>
    );
  }

  if (showResetConfirm) {
    return (
      <div className="w-full max-w-2xl mx-auto bg-white p-8 md:p-12 shadow-xl border-t-4 border-neutral-900 animate-in fade-in">
        <h3 className="text-2xl font-heading uppercase tracking-widest mb-6">Reset Form</h3>
        <p className="text-lg text-neutral-600 mb-8">
          Are you sure you want to clear this enquiry? This action cannot be undone.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <button 
            onClick={() => setShowResetConfirm(false)}
            className="border border-neutral-300 text-neutral-600 uppercase tracking-widest text-xs font-bold px-8 py-4 hover:bg-neutral-50 transition-colors w-full sm:w-auto text-center"
          >
            Cancel
          </button>
          <button 
            onClick={handleResetConfirm}
            className="bg-red-600 text-white uppercase tracking-widest text-xs font-bold px-8 py-4 hover:bg-red-700 transition-colors w-full sm:w-auto text-center"
          >
            Reset Form
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto bg-white p-8 md:p-12 shadow-xl border-t-4 border-neutral-900">
      {/* Progress Bar */}
      <div className="mb-10">
        <div className="flex justify-between text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2">
          <span>{t.step} {currentStep} {t.of} {TOTAL_STEPS}</span>
          <span>{Math.round((currentStep / TOTAL_STEPS) * 100)}%</span>
        </div>
        <div className="w-full bg-neutral-100 h-2">
          <div 
            className="bg-neutral-900 h-full transition-all duration-300"
            style={{ width: `${(currentStep / TOTAL_STEPS) * 100}%` }}
          />
        </div>
      </div>

      <div className="min-h-[250px]">
        {currentStep === 1 && (
          <div className="animate-in fade-in slide-in-from-right-4">
            <h3 className="text-2xl font-heading uppercase tracking-widest mb-6">{t.step} 1: {t.s1Title}</h3>
            {renderSingleSelect('projectType', t.s1Opts)}
          </div>
        )}
        
        {currentStep === 2 && (
          <div className="animate-in fade-in slide-in-from-right-4">
            <h3 className="text-2xl font-heading uppercase tracking-widest mb-6">{t.step} 2: {t.s2Title}</h3>
            {renderSingleSelect('projectStage', t.s2Opts)}
          </div>
        )}
        
        {currentStep === 3 && (
          <div className="animate-in fade-in slide-in-from-right-4">
            <h3 className="text-2xl font-heading uppercase tracking-widest mb-6">{t.step} 3: {t.s3Title}</h3>
            <input 
              type="text" 
              placeholder={t.s3Ph}
              className="w-full border-b-2 border-neutral-300 focus:border-neutral-900 outline-none py-3 bg-transparent transition-colors text-lg"
              value={formData.projectLocation}
              onChange={(e) => updateData('projectLocation', e.target.value)}
            />
          </div>
        )}
        
        {currentStep === 4 && (
          <div className="animate-in fade-in slide-in-from-right-4">
            <h3 className="text-2xl font-heading uppercase tracking-widest mb-6">{t.step} 4: {t.s4Title}</h3>
            {renderSingleSelect('budget', t.s4Opts)}
          </div>
        )}
        
        {currentStep === 5 && (
          <div className="animate-in fade-in slide-in-from-right-4">
            <h3 className="text-2xl font-heading uppercase tracking-widest mb-6">{t.step} 5: {t.s5Title}</h3>
            {renderMultiSelect('services', t.s5Opts)}
          </div>
        )}
        
        {currentStep === 6 && (
          <div className="animate-in fade-in slide-in-from-right-4">
            <h3 className="text-2xl font-heading uppercase tracking-widest mb-6">{t.step} 6: {t.s6Title}</h3>
            {renderMultiSelect('priorities', t.s6Opts)}
          </div>
        )}
        
        {currentStep === 7 && (
          <div className="animate-in fade-in slide-in-from-right-4">
            <h3 className="text-2xl font-heading uppercase tracking-widest mb-6">{t.step} 7: {t.s7Title}</h3>
            {renderSingleSelect('timeline', t.s7Opts)}
          </div>
        )}
        
        {currentStep === 8 && (
          <div className="animate-in fade-in slide-in-from-right-4">
            <h3 className="text-2xl font-heading uppercase tracking-widest mb-6">{t.step} 8: {t.s8Title}</h3>
            <textarea 
              placeholder={t.s8Ph}
              rows={4}
              className="w-full border-b-2 border-neutral-300 focus:border-neutral-900 outline-none py-3 bg-transparent transition-colors text-lg resize-none"
              value={formData.additionalInfo}
              onChange={(e) => updateData('additionalInfo', e.target.value)}
            />
          </div>
        )}

        {currentStep === TOTAL_STEPS && (
          <div className="animate-in fade-in slide-in-from-right-4">
            <h3 className="text-2xl font-heading uppercase tracking-widest mb-6">{t.step} 9: {t.s9Title}</h3>
            <div className="space-y-6">
              <input 
                type="text" 
                placeholder={t.s9Ph1}
                className="w-full border-b-2 border-neutral-300 focus:border-neutral-900 outline-none py-3 bg-transparent transition-colors text-lg"
                value={formData.name}
                onChange={(e) => updateData('name', e.target.value)}
              />
              <input 
                type="email" 
                placeholder={t.s9Ph2}
                className="w-full border-b-2 border-neutral-300 focus:border-neutral-900 outline-none py-3 bg-transparent transition-colors text-lg"
                value={formData.email}
                onChange={(e) => updateData('email', e.target.value)}
              />
              <input 
                type="tel" 
                placeholder={t.s9Ph3}
                className="w-full border-b-2 border-neutral-300 focus:border-neutral-900 outline-none py-3 bg-transparent transition-colors text-lg"
                value={formData.phone}
                onChange={(e) => updateData('phone', e.target.value)}
              />
            </div>
          </div>
        )}
        
        {error && (
          <div className="mt-6 text-red-600 text-sm font-medium animate-in fade-in">
             <i className="fa-solid fa-circle-exclamation mr-2"></i> {error}
          </div>
        )}
      </div>

      <div className="mt-10 flex flex-col-reverse sm:flex-row justify-between items-center gap-4">
        <div className="flex justify-between w-full sm:w-auto gap-2">
          <button 
            onClick={handlePrev}
            disabled={currentStep === 1 || isSubmitting}
            className={`uppercase tracking-widest text-xs font-bold px-6 py-3 transition-colors ${currentStep === 1 ? 'opacity-0 pointer-events-none' : 'text-neutral-500 hover:text-neutral-900'}`}
          >
            {t.back}
          </button>
          <button 
            onClick={() => setShowResetConfirm(true)}
            disabled={isSubmitting}
            className="uppercase tracking-widest text-xs font-bold px-6 py-3 text-neutral-400 hover:text-red-600 transition-colors"
          >
            Reset
          </button>
        </div>
        <button 
          onClick={handleNext}
          disabled={isSubmitting}
          className="w-full sm:w-auto bg-neutral-900 text-white uppercase tracking-widest text-xs font-bold px-8 py-3 hover:bg-neutral-800 transition-colors disabled:opacity-70 flex items-center justify-center gap-2"
        >
          {isSubmitting && <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>}
          {currentStep === TOTAL_STEPS ? (isSubmitting ? t.submitting : t.submit) : t.continue}
        </button>
      </div>

      <div className="mt-8 text-center border-t border-neutral-100 pt-6">
        <p className="text-xs text-neutral-400">
          Your enquiry is temporarily saved on this device until submitted or cleared.
        </p>
      </div>
    </div>
  );
};
